import { useState, useEffect } from 'react';
import { BotManagement } from '../BotManagement';
import { useAuth } from '../../hooks/useApi';
import { apiClient } from '../../lib/api';
import { lexBotApi } from '../../lib/lexBotApi';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { AlertCircle, CreditCard } from 'lucide-react';
import { useTenantContext } from '../../contexts/TenantContext';
import { Badge } from '../ui/badge';
import { Alert } from '../ui/alert';
import { useNavigate } from 'react-router-dom';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

type BotType = 'shared' | 'custom';

interface BotMetadata {
  botId: string;
  botAliasId: string;
  botLocaleId: string;
  status: string;
  lastUpdatedAt: string;
  enabled: boolean;
}

export function QueueAI() {
  const { isAuthenticated } = useAuth();
  const { selectedTenantId } = useTenantContext();
  const navigate = useNavigate();
  const [tenantPlan, setTenantPlan] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Bot metadata
  const [sharedBot, setSharedBot] = useState<BotMetadata | null>(null);
  const [customBot, setCustomBot] = useState<BotMetadata | null>(null);
  const [selectedBot, setSelectedBot] = useState<BotType>('shared');
  const [tenantSettings, setTenantSettings] = useState<any>({});
  const [savingSettings, setSavingSettings] = useState(false);

  const isEnterprise = tenantPlan === 'enterprise';
  const isTrial = tenantPlan === 'trial';
  const showResponseEnhancement = isEnterprise || isTrial;

  useEffect(() => {
    if (isAuthenticated && selectedTenantId) {
      loadData();
    }
  }, [isAuthenticated, selectedTenantId]);

  const loadData = async () => {
    if (!selectedTenantId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Load tenant plan and settings
      const settings = await apiClient.getTenantSettings(selectedTenantId);
      setTenantPlan(settings.plan || '');
      setTenantSettings(settings.settings || {});

      // Load shared bot metadata (hardcoded default bot)
      setSharedBot({
        botId: 'TCHLAQX9JH',
        botAliasId: 'MKBW13Y8OC',
        botLocaleId: 'en_US',
        status: 'READY',
        lastUpdatedAt: new Date().toISOString(),
        enabled: !settings.settings?.custom_lex_enabled
      });

      // Load custom bot if exists - check settings first
      if (settings.plan === 'enterprise' && settings.settings?.queue_ai) {
        const queueAI = settings.settings.queue_ai;
        
        // Bot exists if we have a botId in settings
        if (queueAI.botId) {
          const customBotEnabled = queueAI.custom_lex_enabled || false;
          setCustomBot({
            botId: queueAI.botId,
            botAliasId: queueAI.botAliasId || 'TSTALIASID',
            botLocaleId: queueAI.botLocaleId || 'en_US',
            status: queueAI.status || 'READY',
            lastUpdatedAt: queueAI.lastUpdatedAt || new Date().toISOString(),
            enabled: customBotEnabled
          });
          
          // Set selected bot based on enabled state
          setSelectedBot(customBotEnabled ? 'custom' : 'shared');
          
          // Update shared bot enabled state
          if (sharedBot) {
            setSharedBot({ ...sharedBot, enabled: !customBotEnabled });
          }
        } else {
          // No custom bot exists, ensure shared bot is selected
          setSelectedBot('shared');
        }
      } else {
        // Not enterprise or no queue_ai settings, ensure shared bot is selected
        setSelectedBot('shared');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load bot configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleResponseEnhancementToggle = async (enabled: boolean) => {
    if (!selectedTenantId || !showResponseEnhancement) return;
    
    setSavingSettings(true);
    setError(null);
    
    try {
      const settingsUpdate = {
        settings: {
          ai_response_enhancement: enabled
        }
      };
      
      await apiClient.updateTenantSettings(selectedTenantId, settingsUpdate);
      
      const updatedSettings = {
        ...tenantSettings,
        ai_response_enhancement: enabled
      };
      setTenantSettings(updatedSettings);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update response enhancement setting');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleToneChange = async (tone: string) => {
    if (!selectedTenantId || !showResponseEnhancement) return;
    
    setSavingSettings(true);
    setError(null);
    
    try {
      const settingsUpdate = {
        settings: {
          ai_tone_of_voice: tone
        }
      };
      
      await apiClient.updateTenantSettings(selectedTenantId, settingsUpdate);
      
      const updatedSettings = {
        ...tenantSettings,
        ai_tone_of_voice: tone
      };
      setTenantSettings(updatedSettings);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update tone of voice');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSwitchBot = async (botType: BotType) => {
    if (!selectedTenantId) return;
    
    // Allow switching to shared bot even without custom bot
    if (botType === 'custom' && !customBot) {
      setError('No custom bot available. Create one first.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      if (botType === 'custom') {
        await lexBotApi.enableBot(selectedTenantId);
        // Update custom bot enabled state
        if (customBot) {
          setCustomBot({ ...customBot, enabled: true });
        }
      } else {
        // Switching to shared bot - disable custom if it exists
        if (customBot) {
          await lexBotApi.disableBot(selectedTenantId);
          setCustomBot({ ...customBot, enabled: false });
        }
      }
      
      // Update shared bot state
      if (sharedBot) {
        setSharedBot({ ...sharedBot, enabled: botType === 'shared' });
      }
      
      setSelectedBot(botType);
      await loadData(); // Refresh state
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to switch to ${botType} bot`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomBot = async () => {
    if (!selectedTenantId || !customBot) return;
    if (!confirm('Delete custom bot? This cannot be undone.')) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await lexBotApi.deleteBot(selectedTenantId);
      setCustomBot(null);
      setSelectedBot('shared');
      await loadData(); // Refresh state
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete bot');
    } finally {
      setLoading(false);
    }
  };



  const currentBot = selectedBot === 'custom' && customBot ? customBot : sharedBot;

  if (loading && !currentBot) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <span className="text-2xl">🤖</span>
            QueueAI
            {tenantPlan && (
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm px-2 py-1 rounded-full capitalize">
                {tenantPlan}
              </span>
            )}
          </h1>
          <p className="text-muted-foreground">
            Intelligent SMS assistant with AI capabilities. View configuration, intents, and response settings.
          </p>
        </div>

        {/* Contextual Header Actions */}
        <div className="flex items-center gap-3">
          {!isEnterprise ? (
            // Non-Enterprise: Upsell
            <Button
              onClick={() => navigate('/billing')}
              variant="default"
              size="sm"
              data-testid="qa-go-to-billing-btn"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Go to Billing
            </Button>
          ) : customBot ? (
            // Enterprise with custom bot: Active Bot selector
            <div className="flex items-center gap-2 border rounded-md p-2" data-testid="qa-active-bot-select">
              <span className="text-sm font-medium">Active Bot:</span>
              <Button
                variant={selectedBot === 'shared' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleSwitchBot('shared')}
                disabled={loading}
              >
                Shared
              </Button>
              <Button
                variant={selectedBot === 'custom' ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleSwitchBot('custom')}
                disabled={loading || !customBot}
              >
                Custom
              </Button>
            </div>
          ) : (
            // Enterprise without custom bot: Show create option
            <div className="text-sm text-muted-foreground">
              No custom bot configured
            </div>
          )}


        </div>
      </div>

      {/* Non-Enterprise Upsell Banner */}
      {!isEnterprise && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <div>
            <p className="font-semibold">Upgrade to Enterprise</p>
            <p className="text-sm">Create a custom AI bot with personalized intents and sample utterances for your business.</p>
          </div>
        </Alert>
      )}

      {/* Error Display */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <p>{error}</p>
          <Button onClick={loadData} variant="outline" size="sm" className="mt-2">
            Retry
          </Button>
        </Alert>
      )}

      {/* AI Response Enhancement (Enterprise + Trial) */}
      {showResponseEnhancement && (
        <Card>
          <CardHeader>
            <CardTitle>AI Response Enhancement</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium">Enhanced Response Formatting</p>
                <p className="text-sm text-muted-foreground">
                  Enhance response tone and formatting for more natural, conversational SMS messages.
                </p>
              </div>
              <div className="flex items-center gap-3">
                {tenantSettings.ai_response_enhancement && (
                  <Badge variant="default" className="text-xs">
                    Enabled
                  </Badge>
                )}
                <Switch
                  checked={tenantSettings.ai_response_enhancement || false}
                  onCheckedChange={handleResponseEnhancementToggle}
                  disabled={savingSettings}
                  data-testid="qa-response-enhancement-toggle"
                />
                {savingSettings && (
                  <span className="text-sm text-muted-foreground ml-2">Saving...</span>
                )}
              </div>
            </div>
            {tenantSettings.ai_response_enhancement && (
              <>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Tone of Voice</label>
                  <Select
                    value={tenantSettings.ai_tone_of_voice || 'professional'}
                    onValueChange={handleToneChange}
                    disabled={savingSettings}
                  >
                    <SelectTrigger className="w-full" data-testid="qa-tone-select">
                      <SelectValue placeholder="Select tone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional - Business-appropriate, polite, clear</SelectItem>
                      <SelectItem value="casual">Casual - Friendly and conversational</SelectItem>
                      <SelectItem value="humorous">Humorous - Light, witty, and playful</SelectItem>
                      <SelectItem value="sarcastic">Sarcastic - Clever and slightly cheeky</SelectItem>
                      <SelectItem value="empathetic">Empathetic - Understanding and caring</SelectItem>
                      <SelectItem value="enthusiastic">Enthusiastic - Energetic and upbeat</SelectItem>
                      <SelectItem value="concise">Concise - Brief and to-the-point</SelectItem>
                      <SelectItem value="warm">Warm - Kind and welcoming</SelectItem>
                      <SelectItem value="formal">Formal - Courteous and respectful</SelectItem>
                      <SelectItem value="cheerful">Cheerful - Bright and positive</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Choose the tone for enhanced bot responses
                  </p>
                </div>
                <div className="bg-blue-50 p-3 rounded-md">
                  <p className="text-sm text-blue-800">
                    ✨ AI enhancement is enabled with <span className="font-semibold">{tenantSettings.ai_tone_of_voice || 'professional'}</span> tone.
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content */}
      {!isEnterprise ? (
        // Show wizard/creation flow for non-enterprise
        <BotManagement tenantId={selectedTenantId || ''} planType={tenantPlan} />
      ) : !customBot ? (
        // Show wizard/creation flow for enterprise without custom bot
        <BotManagement tenantId={selectedTenantId || ''} planType={tenantPlan} />
      ) : (
        // Show simple overview for enterprise with custom bot
        <div className="space-y-4">
          {currentBot && (
            <Card data-testid="qa-overview-card">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{selectedBot === 'custom' ? 'Custom' : 'Shared'} Bot Overview</span>
                  <Badge 
                    variant={currentBot.status === 'READY' ? 'default' : currentBot.status === 'FAILED' ? 'destructive' : 'secondary'}
                    data-testid="qa-status-chip"
                  >
                    {currentBot.status}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div><span className="font-medium">Bot ID:</span> {currentBot.botId}</div>
                <div><span className="font-medium">Alias:</span> {currentBot.botAliasId}</div>
                <div><span className="font-medium">Locale:</span> {currentBot.botLocaleId}</div>
                <div><span className="font-medium">Updated:</span> {new Date(currentBot.lastUpdatedAt).toLocaleString()}</div>
                <div><span className="font-medium">Enabled:</span> {currentBot.enabled ? 'Yes' : 'No'}</div>
              </CardContent>
            </Card>
          )}

          {/* Danger Zone - Only show for custom bot */}
          {selectedBot === 'custom' && customBot && (
            <Card>
              <CardHeader>
                <CardTitle>Danger Zone</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Deleting the custom bot will revert to using the shared bot.
                </p>
                <Button
                  onClick={handleDeleteCustomBot}
                  disabled={loading}
                  variant="destructive"
                  data-testid="qa-delete-custom-btn"
                >
                  Delete Custom Bot
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
